import React from 'react';
import { Container } from '../landingPage/components/Container';

export default function PrivacyPage() {
  return (
    <Container className="py-16">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-4xl font-bold text-gray-900 mb-8">Privacy Policy</h1>
        <p className="text-sm text-gray-600 mb-8">Last updated: 03/10/2024</p>

        <div className="prose prose-sm max-w-none">
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Introduction</h2>
            <p>
              Welcome to EnableSEND.co.uk ("Company", "we", "our", "us"). We are committed to protecting your personal data and your privacy. 
              This privacy policy will inform you as to how we look after your personal data when you visit our website (regardless of where you visit it from) 
              and tell you about your privacy rights and how the law protects you.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Important Information and Who We Are</h2>
            <h3 className="text-xl font-medium text-gray-900 mt-6 mb-3">Purpose of this Privacy Policy</h3>
            <p>
              This privacy policy aims to give you information on how EnableSEND.co.uk collects and processes your personal data through your use of this website, 
              including any data you may provide through this website when you sign up for a subscription, purchase a product or service, or take part in a survey or competition.
            </p>
            <h3 className="text-xl font-medium text-gray-900 mt-6 mb-3">Controller</h3>
            <p>EnableSEND.co.uk is the controller and responsible for your personal data.</p>
            <h3 className="text-xl font-medium text-gray-900 mt-6 mb-3">Contact Details</h3>
            <p>Our contact details are:</p>
            <p>EnableSEND Ltd</p>
            <p className="text-blue-600">customerservice@enablesend.co.uk</p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. The Data We Collect About You</h2>
            <p>We may collect, use, store, and transfer different kinds of personal data about you, which we have grouped together follows:</p>
            <ul className="list-disc pl-6 space-y-2 mt-4">
              <li><strong>Identity Data:</strong> includes first name, maiden name, last name, username or similar identifier, title, date of birth, and gender.</li>
              <li><strong>Contact Data:</strong> includes billing address, delivery address, email address, and telephone numbers.</li>
              <li><strong>Financial Data:</strong> includes bank account and payment card details.</li>
              <li><strong>Transaction Data:</strong> includes details about payments to and from you and other details of products and services you have purchased from us.</li>
              <li><strong>Technical Data:</strong> includes internet protocol (IP) address, your login data, browser type and version, time zone setting and location, browser plug-in types and versions, operating system and platform, and other technology on the devices you use to access this website.</li>
              <li><strong>Profile Data:</strong> includes your username and password, purchases or orders made by you, your interests, preferences, feedback, and survey responses.</li>
              <li><strong>Usage Data:</strong> includes information about how you use our website, products, and services.</li>
              <li><strong>Marketing and Communications Data:</strong> includes your preferences in receiving marketing from us and your communication preferences.</li>
            </ul>
            <p className="mt-4">This data may be shared with local and central government bodies in accordance with strict GDPR (law) protocols.</p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. How We Collect Your Data</h2>
            <p>We use different methods to collect data from and about you including:</p>
            <ul className="list-disc pl-6 space-y-2 mt-4">
              <li><strong>Direct interactions:</strong> You may give us your Identity, Contact, and Marketing and Communications Data by filling in forms or by corresponding with us by post, phone, email, or otherwise.</li>
              <li><strong>Automated technologies or interactions:</strong> As you interact with our website, we may automatically collect Technical Data about your equipment, browsing actions, and patterns.</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. How We Use Your Data</h2>
            <p>We will only use your personal data when the law allows us to. Most commonly, we will use your personal data in the following circumstances:</p>
            <ul className="list-disc pl-6 space-y-2 mt-4">
              <li>To register you as a new customer.</li>
              <li>To process and deliver your order.</li>
              <li>To manage our relationship with you.</li>
              <li>To administer and protect our business and this website.</li>
              <li>To deliver relevant website content and advertisements to you.</li>
              <li>To use data analytics to improve our website, products/services, marketing, customer relationships, and experiences.</li>
              <li>To comply with legal obligations and respond to lawful requests by public authorities.</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Legal Basis for Processing Your Data</h2>
            <p>We process your personal data based on the following legal grounds:</p>
            <ul className="list-disc pl-6 space-y-2 mt-4">
              <li><strong>Consent:</strong> You have given clear consent for us to process your personal data for a specific purpose.</li>
              <li><strong>Contract:</strong> The processing is necessary for a contract we have with you.</li>
              <li><strong>Legal obligation:</strong> The processing is necessary for us to comply with the law.</li>
              <li><strong>Legitimate interests:</strong> The processing is necessary for our legitimate interests or those of a third party, provided your interests and fundamental rights do not override those interests.</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Data Sharing</h2>
            <p>We may share your personal data with:</p>
            <ul className="list-disc pl-6 space-y-2 mt-4">
              <li><strong>Service Providers:</strong> Companies or individuals that provide services on our behalf, such as payment processing, data analysis, email delivery, hosting services, and customer service.</li>
              <li><strong>Business Partners:</strong> Partners with whom we collaborate to offer products, services, or promotions.</li>
              <li><strong>Affiliates:</strong> Companies within our corporate family.</li>
              <li><strong>Regulators:</strong> Regulators and other authorities who require reporting of processing activities.</li>
              <li><strong>Advisors:</strong> Professional advisers including lawyers, banks, auditors, and insurers.</li>
              <li><strong>Governmental:</strong> Central and local government parties where required as part of contractual arrangements.</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. International Transfers</h2>
            <p>
              Some of our external third parties are based outside the European Economic Area (EEA) so their processing of your personal data will involve a transfer of data outside the EEA. 
              Whenever we transfer your personal data out of the EEA, we ensure a similar degree of protection is afforded to it.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Data Security</h2>
            <p>
              We have put in place appropriate security measures to prevent your personal data from being accidentally lost, used, or accessed in an unauthorized way, altered, or disclosed.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Data Retention</h2>
            <p>
              We will only retain your personal data for as long as necessary to fulfill the purposes we collected it for, including for the purposes of satisfying any legal, accounting, or reporting requirements.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. Your Legal Rights</h2>
            <p>Under certain circumstances, you have rights under data protection laws in relation to your personal data, including the right to:</p>
            <ul className="list-disc pl-6 space-y-2 mt-4">
              <li>Request access to your personal data.</li>
              <li>Request correction of your personal data.</li>
              <li>Request erasure of your personal data.</li>
              <li>Object to processing of your personal data.</li>
              <li>Request restriction of processing your personal data.</li>
              <li>Request transfer of your personal data.</li>
              <li>Withdraw consent.</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">12. How to Contact Us</h2>
            <p>If you have any questions about this privacy policy or our privacy practices, please contact us:</p>
            <p className="text-blue-600 mt-2">Email: customerservice@enablesend.co.uk</p>
            <p className="mt-4">
              You also have the right to make a complaint at any time to the Information Commissioner's Office (ICO), 
              the UK supervisory authority for data protection issues (<a href="https://www.ico.org.uk" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">www.ico.org.uk</a>).
            </p>
          </section>
        </div>
      </div>
    </Container>
  );
} 